import { ConfiguratorState } from '@/app/store/configurator/types/configurator.types';
import {
	MODULAR_SHELVES,
	RACK_BORDER_MATERIAL_COLORS,
	SHELF_CHUNKS_COUNT,
	SHELVE_MATERIAL_COLORS,
} from '@/shared/constants/configurator';
import { ShelfDeck } from '@/shared/types/configurator';
import { MODEL_DISPLAY_SCALE } from '../constants';
import { Beam } from '../render-components/beam';
import { RenderComponentStrategy } from '../render-components/render-component.strategy';
import { Shelf } from '../render-components/shelf';
import { ShelfOptions, Vector } from '../types/model.types';

export class ShelfBuilder {
	private components: RenderComponentStrategy[] = [];

	constructor(private readonly state: ConfiguratorState) {}

	build(options: ShelfOptions) {
		this.components = [];

		this.addXBorders(options);
		this.addShelfComponents(options);

		return this.components;
	}

	private addXBorders({ position, width, depth }: ShelfOptions) {
		const xPosition = position.x + (width * MODEL_DISPLAY_SCALE) / 2;
		const yPosition = position.y - 0.015;
		const zDeviation = 0.000001;

		const beams: {
			position: Vector;
			rotation: Vector;
		}[] = [
			{
				position: [xPosition, yPosition, position.z + zDeviation],
				rotation: [Math.PI / 2, 0, Math.PI / 2],
			},
			{
				position: [
					xPosition,
					yPosition,
					position.z - zDeviation - depth * MODEL_DISPLAY_SCALE,
				],
				rotation: [Math.PI / 2, 0, -Math.PI / 2],
			},
		];

		for (const beam of beams) {
			this.components.push(
				new Beam({
					...beam,
					color: RACK_BORDER_MATERIAL_COLORS[this.state.material],
					scale: [1, this.getShelfBordersScale(width), 0.6],
				}),
			);
		}
	}

	private addShelfComponents({ position, deck, depth, width }: ShelfOptions) {
		const availableWidth = width - 62;
		const shelfComponentWidth = (availableWidth * MODEL_DISPLAY_SCALE) / SHELF_CHUNKS_COUNT;

		const yPosition = position.y + 0.01;
		const zPosition = position.z - (depth * MODEL_DISPLAY_SCALE) / 2;
		const initialXPosition = 31 * MODEL_DISPLAY_SCALE;

		const zScale = this.getShelfDepthScale(depth);
		const xScale = this.getShelfWidthScale(width, deck);

		const scale: Vector = [zScale, xScale, 0.75];
		const rotation: Vector = [Math.PI / 2, 0, Math.PI / 2];

		for (let i = 0; i < SHELF_CHUNKS_COUNT; i++) {
			const xPosition =
				position.x + shelfComponentWidth / 2 + initialXPosition + i * shelfComponentWidth;

			this.components.push(
				new Shelf({
					position: [xPosition, yPosition, zPosition],
					color: SHELVE_MATERIAL_COLORS[deck],
					rotation,
					scale,
				}),
			);
		}
	}

	private getShelfDepthScale(shelfDepth: number) {
		const percent = shelfDepth / 1080;
		return percent;
	}

	private getShelfWidthScale(shelfWidth: number, shelfDeck: ShelfDeck) {
		const baseWidthScales: Record<number, number> = {
			1340: 0.47,
			1720: 0.61,
			2100: 0.76,
			2170: 0.81,
			2270: 0.81,
			2480: 0.885,
		};
		const baseWidthScale = baseWidthScales[shelfWidth];
		const result = MODULAR_SHELVES.includes(shelfDeck) ? baseWidthScale : baseWidthScale + 0.01;

		return result;
	}

	private getShelfBordersScale(shelfWidth: number) {
		const results: Record<number, number> = {
			1340: 0.463,
			1720: 0.598,
			2100: 0.75,
			2170: 0.793,
			2270: 0.793,
			2480: 0.865,
		};

		return results[shelfWidth];
	}
}
